import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';
import { Heart, TrendingUp, PieChart, Lightbulb, Sparkles, IndianRupee, Users, Music, Camera, Utensils } from 'lucide-react';

const InteractiveMarriagePlanner = () => {
  const [inputs, setInputs] = useState({
    weddingBudget: 2500000,
    yearsToWedding: 3,
    expectedReturn: 12,
    currentSavings: 0
  });
  const [result, setResult] = useState(null);
  const [breakdown, setBreakdown] = useState(null);

  const calculateWeddingFund = useCallback(() => {
    const target = parseFloat(inputs.weddingBudget) - parseFloat(inputs.currentSavings || 0);
    const r = parseFloat(inputs.expectedReturn) / 12 / 100;
    const n = parseInt(inputs.yearsToWedding) * 12;
    const inflationRate = 0.06;
    const years = parseInt(inputs.yearsToWedding);
    
    const inflatedTarget = target * Math.pow(1 + inflationRate, years);

    if (r === 0 || n === 0) {
      setResult({
        monthlySIP: Math.round(inflatedTarget / Math.max(n, 1)),
        totalInvested: Math.round(inflatedTarget),
        wealthGained: 0,
        targetAmount: Math.round(inflatedTarget),
        originalTarget: parseFloat(inputs.weddingBudget)
      });
      return;
    }

    const monthlySIP = inflatedTarget * r / (((Math.pow(1 + r, n) - 1)) * (1 + r));
    const totalInvested = monthlySIP * n;
    const wealthGained = inflatedTarget - totalInvested;

    setResult({
      monthlySIP: Math.round(monthlySIP),
      totalInvested: Math.round(totalInvested),
      wealthGained: Math.round(wealthGained),
      targetAmount: Math.round(inflatedTarget),
      originalTarget: parseFloat(inputs.weddingBudget)
    });

    const budget = parseFloat(inputs.weddingBudget);
    setBreakdown({
      venue: Math.round(budget * 0.25),
      catering: Math.round(budget * 0.20),
      decoration: Math.round(budget * 0.15),
      photography: Math.round(budget * 0.10),
      jewelry: Math.round(budget * 0.15),
      clothing: Math.round(budget * 0.10),
      miscellaneous: Math.round(budget * 0.05)
    });
  }, [inputs]);

  useEffect(() => {
    calculateWeddingFund();
  }, [calculateWeddingFund]);

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const weddingPresets = [
    { name: 'Simple Wedding', amount: 1000000 },
    { name: 'Traditional Wedding', amount: 2500000 },
    { name: 'Grand Wedding', amount: 5000000 },
    { name: 'Destination Wedding', amount: 10000000 }
  ];

  const sliderStyles = `
    .slider-rose::-webkit-slider-thumb {
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #f43f5e, #ec4899);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(244, 63, 94, 0.5);
    }
    .slider-rose::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #f43f5e, #ec4899);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(244, 63, 94, 0.5);
      border: none;
    }
  `;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      <style dangerouslySetInnerHTML={{ __html: sliderStyles }} />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-rose-500 to-pink-500 rounded-2xl mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Marriage Planning Calculator
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Plan your dream wedding without financial stress. Start saving today for your special day!
            </p>
          </div>

          <div className="mb-8">
            <p className="text-white/60 text-sm mb-3 text-center">Choose Wedding Style:</p>
            <div className="flex flex-wrap justify-center gap-3">
              {weddingPresets.map((preset, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputs({ ...inputs, weddingBudget: preset.amount })}
                  className={`px-4 py-2 border rounded-full text-sm transition-all ${
                    inputs.weddingBudget === preset.amount
                      ? 'bg-rose-500/20 border-rose-500/50 text-rose-400'
                      : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-rose-400" />
                Wedding Details
              </h2>
              
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Wedding Budget</label>
                    <span className="text-rose-400 font-bold text-xl">{formatCurrency(inputs.weddingBudget)}</span>
                  </div>
                  <input
                    type="range"
                    min="500000"
                    max="20000000"
                    step="100000"
                    value={inputs.weddingBudget}
                    onChange={(e) => setInputs({...inputs, weddingBudget: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-rose"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>₹5 L</span>
                    <span>₹2 Cr</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Years to Wedding</label>
                    <span className="text-pink-400 font-bold text-xl">{inputs.yearsToWedding} Years</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={inputs.yearsToWedding}
                    onChange={(e) => setInputs({...inputs, yearsToWedding: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-rose"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>1 Year</span>
                    <span>10 Years</span>
                  </div>
                </div>

                <div>
                  <label className="text-white font-medium mb-2 block">Current Savings (if any)</label>
                  <input
                    type="number"
                    value={inputs.currentSavings}
                    onChange={(e) => setInputs({...inputs, currentSavings: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-rose-500"
                    placeholder="0"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Expected Return (p.a.)</label>
                    <span className="text-green-400 font-bold text-xl">{inputs.expectedReturn}%</span>
                  </div>
                  <input
                    type="range"
                    min="6"
                    max="15"
                    step="0.5"
                    value={inputs.expectedReturn}
                    onChange={(e) => setInputs({...inputs, expectedReturn: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-rose"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>6%</span>
                    <span>15%</span>
                  </div>
                </div>
              </div>
            </div>

            {result && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-rose-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-8 border border-rose-500/30">
                  <p className="text-white/60 text-sm mb-2">Your Dream Wedding Fund</p>
                  <div className="flex items-center gap-3 mb-4">
                    <IndianRupee className="w-6 h-6 text-rose-400" />
                    <h3 className="text-white/70 text-sm">Monthly SIP Required</h3>
                  </div>
                  <p className="text-5xl font-bold text-white mb-2">{formatCurrency(result.monthlySIP)}</p>
                  <p className="text-white/50 text-sm">for {inputs.yearsToWedding} years</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <PieChart className="w-4 h-4 text-white/50" />
                      <p className="text-white/50 text-xs">Your Investment</p>
                    </div>
                    <p className="text-2xl font-bold text-white">{formatCurrency(result.totalInvested)}</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <p className="text-white/50 text-xs">Returns Earned</p>
                    </div>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(result.wealthGained)}</p>
                  </div>
                </div>

                {breakdown && (
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <p className="text-white font-semibold mb-4">Wedding Budget Breakdown</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-rose-400" />
                        <span className="text-white/60">Venue:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.venue)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Utensils className="w-4 h-4 text-orange-400" />
                        <span className="text-white/60">Catering:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.catering)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        <span className="text-white/60">Decoration:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.decoration)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Camera className="w-4 h-4 text-blue-400" />
                        <span className="text-white/60">Photography:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.photography)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Heart className="w-4 h-4 text-pink-400" />
                        <span className="text-white/60">Jewelry:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.jewelry)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Music className="w-4 h-4 text-cyan-400" />
                        <span className="text-white/60">Clothing:</span>
                        <span className="text-white ml-auto">{formatCurrency(breakdown.clothing)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-xl rounded-2xl p-6 border border-amber-500/30">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-semibold mb-2">Smart Saving Tip</p>
                      <p className="text-white/80 text-sm">
                        {inputs.yearsToWedding <= 2 
                          ? "With less than 2 years, consider low-risk options like liquid funds or short-term debt funds to protect your corpus."
                          : inputs.yearsToWedding <= 5
                          ? "For 3-5 years horizon, a balanced approach with 60% equity and 40% debt funds can optimize returns while managing risk."
                          : "With 5+ years, you can afford to take more equity exposure initially and gradually shift to debt as the wedding approaches."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(244,63,94,0.4)] transition-all duration-300"
            >
              Get Expert Wedding Planning Advice
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InteractiveMarriagePlanner;
